import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { COUPON_TYPE_OPTIONS } from "./schema";
import {
  CouponDiscountType,
  CouponStatus,
  CouponType,
  ListCouponsQuery,
} from "../../../redux/features/types/couponListTypes";
import { DateRangeType } from "../../ui/DateRangeSelector";
import { buildListCouponQuery } from "./buildListCouponQuery";

// ---------------------------------------------------------------------------
// useCouponFilters
//
// Single source of truth for the All Coupons / Used Coupons filter state.
// Owns:
//   - Typed-vs-applied state for the two text inputs (debounced apply).
//   - Categorical + date filter state.
//   - Pagination (page / limit), 0-based for the UI.
//   - URL <-> state sync (so filters survive navigation and are shareable).
//   - "Reset key" for force-remounting controls that seed from props on mount.
//   - Memoized `query` ready to pass to fetchAllCoupons / fetchAllUsedCoupons.
//
// All filter setters auto-reset `page` to 0 — changing a filter should always
// return the user to the first page of results.
// ---------------------------------------------------------------------------

/**
 * URL parameter keys this hook owns. The sync effect deletes these from the
 * existing URL before re-setting active ones so it never clobbers params
 * managed by the parent page (e.g. `?tab=cashback`).
 */
const MANAGED_URL_KEYS = [
  "search",
  "buyerName",
  "couponType",
  "status",
  "discountType",
  "createdFrom",
  "createdTo",
  "expiryFrom",
  "expiryTo",
  "page",
  "limit",
] as const;

const isCouponType = (val: string): val is CouponType =>
  (COUPON_TYPE_OPTIONS as readonly string[]).includes(val);

const isCouponStatus = (val: string): val is CouponStatus =>
  val === "active" || val === "expired";

const isDiscountType = (val: string): val is CouponDiscountType =>
  val === "fixed" || val === "percentage";

const parseUrlDate = (val: string | null): Date | null => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseFilters = (sp: URLSearchParams) => {
  const couponType = sp.get("couponType");
  const status = sp.get("status");
  const discountType = sp.get("discountType");
  const page = Number(sp.get("page"));
  const limit = Number(sp.get("limit"));

  return {
    search: sp.get("search") ?? "",
    buyerName: sp.get("buyerName") ?? "",
    couponType: couponType && isCouponType(couponType) ? couponType : null,
    status: status && isCouponStatus(status) ? status : null,
    discountType:
      discountType && isDiscountType(discountType) ? discountType : null,
    createdRange: {
      startDate: parseUrlDate(sp.get("createdFrom")),
      endDate: parseUrlDate(sp.get("createdTo")),
    } as DateRangeType,
    expiryRange: {
      startDate: parseUrlDate(sp.get("expiryFrom")),
      endDate: parseUrlDate(sp.get("expiryTo")),
    } as DateRangeType,
    page: Number.isFinite(page) && page > 0 ? page : 0,
    limit: Number.isFinite(limit) && limit > 0 ? limit : 10,
  };
};

export interface UseCouponFiltersReturn {
  // Typed input state (controlled inputs in the bar)
  inputValue: string;
  setInputValue: (v: string) => void;
  buyerNameInput: string;
  setBuyerNameInput: (v: string) => void;

  // Applied filter state
  search: string;
  buyerName: string;
  couponType: CouponType | null;
  setCouponType: (v: CouponType | null) => void;
  status: CouponStatus | null;
  setStatus: (v: CouponStatus | null) => void;
  discountType: CouponDiscountType | null;
  setDiscountType: (v: CouponDiscountType | null) => void;
  createdRange: DateRangeType;
  setCreatedRange: (r: DateRangeType) => void;
  expiryRange: DateRangeType;
  setExpiryRange: (r: DateRangeType) => void;

  // Pagination (0-based)
  page: number;
  setPage: (v: number) => void;
  limit: number;
  setLimit: (v: number) => void;

  // Bar chrome helpers
  filterResetKey: number;
  /** Force-remount of categorical/date controls (after a scoped reset). */
  bumpFilterResetKey: () => void;
  handleClearFilters: () => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;

  // Composed, API-ready query
  query: ListCouponsQuery;
}

export const useCouponFilters = (): UseCouponFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params ONCE at mount. Subsequent URL changes are driven by the
  // sync effect below — we never re-hydrate from URL after mount.
  const initial = useMemo(
    () => parseFilters(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [inputValue, setInputValue] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [buyerNameInput, setBuyerNameInput] = useState(initial.buyerName);
  const [buyerName, setBuyerName] = useState(initial.buyerName);
  const [couponType, setCouponTypeState] = useState<CouponType | null>(
    initial.couponType
  );
  const [status, setStatusState] = useState<CouponStatus | null>(initial.status);
  const [discountType, setDiscountTypeState] =
    useState<CouponDiscountType | null>(initial.discountType);
  const [createdRange, setCreatedRangeState] = useState<DateRangeType>(
    initial.createdRange
  );
  const [expiryRange, setExpiryRangeState] = useState<DateRangeType>(
    initial.expiryRange
  );
  const [page, setPage] = useState(initial.page);
  const [limit, setLimit] = useState(initial.limit);
  // Bumping this key force-remounts FilterByButton / DateRangeSelector,
  // which both seed internal state from props on mount only.
  const [filterResetKey, setFilterResetKey] = useState(0);

  // Wrapped setters — changing a filter always returns to page 1.
  const setCouponType = useCallback((v: CouponType | null) => {
    setCouponTypeState(v);
    setPage(0);
  }, []);
  const setStatus = useCallback((v: CouponStatus | null) => {
    setStatusState(v);
    setPage(0);
  }, []);
  const setDiscountType = useCallback((v: CouponDiscountType | null) => {
    setDiscountTypeState(v);
    setPage(0);
  }, []);
  const setCreatedRange = useCallback((r: DateRangeType) => {
    setCreatedRangeState(r);
    setPage(0);
  }, []);
  const setExpiryRange = useCallback((r: DateRangeType) => {
    setExpiryRangeState(r);
    setPage(0);
  }, []);

  // Debounced apply for text inputs — 500ms after the user stops typing.
  // The two-state split (typed vs. applied) keeps the input snappy while the
  // API request only fires once per pause.
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(inputValue);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setBuyerName(buyerNameInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [buyerNameInput]);

  // Sync applied filters + pagination to the URL. `replace: true` keeps the
  // browser history clean — typing in a search box should NOT create a new
  // history entry per keystroke.
  //
  // We use the function form of `setSearchParams` and operate ONLY on the
  // keys this hook owns. Anything else in the URL (e.g. the parent page's
  // `?tab=cashback`) must be preserved — otherwise mounting this hook on
  // tab-change wipes the parent's params and the tab snaps back.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of MANAGED_URL_KEYS) next.delete(key);

        if (search) next.set("search", search);
        if (buyerName) next.set("buyerName", buyerName);
        if (couponType) next.set("couponType", couponType);
        if (status) next.set("status", status);
        if (discountType) next.set("discountType", discountType);
        if (createdRange.startDate) {
          next.set(
            "createdFrom",
            new Date(createdRange.startDate).toISOString()
          );
        }
        if (createdRange.endDate) {
          next.set("createdTo", new Date(createdRange.endDate).toISOString());
        }
        if (expiryRange.startDate) {
          next.set("expiryFrom", new Date(expiryRange.startDate).toISOString());
        }
        if (expiryRange.endDate) {
          next.set("expiryTo", new Date(expiryRange.endDate).toISOString());
        }
        if (page > 0) next.set("page", String(page));
        if (limit !== 10) next.set("limit", String(limit));
        return next;
      },
      { replace: true }
    );
  }, [
    search,
    buyerName,
    couponType,
    status,
    discountType,
    createdRange,
    expiryRange,
    page,
    limit,
    setSearchParams,
  ]);

  const bumpFilterResetKey = useCallback(() => {
    setFilterResetKey((k) => k + 1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setInputValue("");
    setSearch("");
    setBuyerNameInput("");
    setBuyerName("");
    setCouponTypeState(null);
    setStatusState(null);
    setDiscountTypeState(null);
    setCreatedRangeState({ startDate: null, endDate: null });
    setExpiryRangeState({ startDate: null, endDate: null });
    setFilterResetKey((k) => k + 1);
    setPage(0);
  }, []);

  const isCreatedRangeActive = !!(createdRange.startDate || createdRange.endDate);
  const isExpiryRangeActive = !!(expiryRange.startDate || expiryRange.endDate);
  const activeFilterCount =
    (search ? 1 : 0) +
    (buyerName ? 1 : 0) +
    (couponType ? 1 : 0) +
    (status ? 1 : 0) +
    (discountType ? 1 : 0) +
    (isCreatedRangeActive ? 1 : 0) +
    (isExpiryRangeActive ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const query = useMemo<ListCouponsQuery>(
    () =>
      buildListCouponQuery({
        page,
        limit,
        search,
        buyerName,
        couponType,
        status,
        discountType,
        createdRange,
        expiryRange,
      }),
    [
      page,
      limit,
      search,
      buyerName,
      couponType,
      status,
      discountType,
      createdRange,
      expiryRange,
    ]
  );

  return {
    inputValue,
    setInputValue,
    buyerNameInput,
    setBuyerNameInput,
    search,
    buyerName,
    couponType,
    setCouponType,
    status,
    setStatus,
    discountType,
    setDiscountType,
    createdRange,
    setCreatedRange,
    expiryRange,
    setExpiryRange,
    page,
    setPage,
    limit,
    setLimit,
    filterResetKey,
    bumpFilterResetKey,
    handleClearFilters,
    activeFilterCount,
    hasActiveFilters,
    query,
  };
};
